import type { QueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { OrderTypeConfig } from '@/hooks/useEnabledOrderTypes'
import { mergePricingSettings } from '@/lib/counterPricing'
import { displayLabelForOrderType } from '@/lib/orderTypeSettings'
import {
  checkoutStoredIntentToReceiptPaymentMethod,
  parseReceiptSettings,
  printCustomerReceipt,
} from '@/lib/printCustomerReceipt'

export type PrintGuestReceiptDeps = {
  formatAmount: (n: number) => string
  getCashierName: () => string
  isDuplicateCopy?: boolean
}

/**
 * Fetches authoritative order + settings + counter pricing + enabled order
 * types (same hooks as counter), then prints a final guest receipt
 * (no PRA audit / numbering).
 */
export async function printGuestReceiptForOrderId(
  api: typeof apiClient,
  queryClient: QueryClient,
  orderId: string,
  deps: PrintGuestReceiptDeps,
): Promise<{ ok: true } | { ok: false; reason: string }> {
  const [orderRes, settingsRes, pricingPayload, orderTypes] = await Promise.all([
    api.getOrder(orderId),
    queryClient.fetchQuery({
      queryKey: ['settings', 'all'],
      queryFn: () => api.getAllSettings(),
    }),
    queryClient.fetchQuery({
      queryKey: ['counterPricing'],
      queryFn: () =>
        api.getCounterPricing().then((r) => (r.success && r.data ? r.data : null)),
    }),
    queryClient.fetchQuery({
      queryKey: ['settings', 'enabled_order_types'],
      queryFn: async () => {
        const res = await api.getSetting('enabled_order_types')
        if (res.success && Array.isArray(res.data)) return res.data as OrderTypeConfig[]
        return []
      },
    }),
  ])

  if (!orderRes.success || !orderRes.data) {
    return { ok: false, reason: orderRes.message || 'Could not load order' }
  }
  if (!settingsRes.success || !settingsRes.data) {
    return { ok: false, reason: settingsRes.message || 'Could not load receipt settings' }
  }
  if (pricingPayload == null) {
    return { ok: false, reason: 'Could not load pricing settings' }
  }
                                                         
  mergePricingSettings(pricingPayload)

  const order = orderRes.data
  const billable = (order.items ?? []).filter((i) => i.status !== 'voided')
  if (order.status !== 'completed' || billable.length === 0) {
    return { ok: false, reason: 'Order not printable' }
  }

  const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
  const paymentMethod = checkoutStoredIntentToReceiptPaymentMethod(order.checkout_payment_method)
  /** Prefer settlement time on historical orders; avoids using created_at for replay. */
  const paidAt = order.completed_at ? new Date(order.completed_at) : new Date()

  const { printed } = printCustomerReceipt(order, cfg, {
    cashierName: deps.getCashierName(),
    paymentMethod,
    paidAt,
    orderTypeLabel: displayLabelForOrderType(order.order_type, orderTypes),
    formatAmount: deps.formatAmount,
    billKind: 'final',
    isReplayCopy: deps.isDuplicateCopy,
  })

  if (!printed) {
    return {
      ok: false,
      reason: 'Popup blocked — allow popups for this site to print',
    }
  }
  return { ok: true }
}
